// The cost model for the screens: one indexCosts() (shared/costs.ts) over the synced tables and the BOM index (with
// prices, without stock: inventory webhooks never rebuild it), rebuilt only when one of them changes and shared by
// every component that asks. Run results, stacks and tooling payoff are
// worked out on demand and cached on it, so a tab switch or a hover costs nothing.

import { type CostData, type CostIndex, indexCosts, type LandedSource, landedCostOf } from "../../../shared/costs.ts";
import type { Attachment } from "../../../shared/db.gen.ts";
import { costedPoLines } from "../../../shared/po.ts";
import { useBomPrices } from "./boms.ts";
import { dayText } from "./format.ts";
import { useRows } from "./sync.ts";

let last: { inputs: unknown[]; index: CostIndex } | null = null;

export function useCostIndex(): CostIndex {
  const ix = useBomPrices();
  const tables = {
    projects: useRows("project"),
    items: useRows("item"),
    vendors: useRows("vendor"),
    vendorItems: useRows("vendor_item"),
    runs: useRows("production_run"),
    runLines: useRows("production_run_line"),
    runSupply: useRows("production_run_supply"), // who supplies a part for a run (migration 057)
    pos: useRows("po"),
    poLines: costedPoLines(useRows("po_line")), // An accepted invoice price is the cost ---
    receipts: useRows("receipt"),
    receiptLines: useRows("receipt_line"),
    landedCosts: useRows("landed_cost"),
    landedCostReceipts: useRows("landed_cost_receipt"),
    landedCostCharges: useRows("landed_cost_charge"),
    makeOrders: useRows("make_order"),
    cmReports: useRows("cm_report"),
    cmReportLines: useRows("cm_report_line"),
    costs: useRows("production_cost"),
    tooling: useRows("tooling_item"),
    freightRates: useRows("freight_rate"),
    dutyRates: useRows("duty_rate"),
    pricePoints: useRows("price_point"),
    attachments: useRows("attachment"),
    bills: useRows("bill"), // Bills and payments --- "Billed / Paid" on Costs & invoices
    billLines: useRows("bill_line"),
    payments: useRows("payment"), // a fee that may be one entered by hand: Needs attention
    poLineCharges: useRows("po_line_charge"), // Charges --- what a make PO line's fee is made of
    payAccounts: useRows("pay_account"), // Which card --- "Paid by Amex ••1005"
  };
  const inputs = [ix, ...Object.values(tables)];
  if (!last || last.inputs.some((x, i) => x !== inputs[i])) last = { inputs, index: indexCosts(ix, tables as unknown as CostData) };
  return last.index;
}

let lastFiles: { rows: unknown; byOwner: Map<string, Attachment[]> } | null = null;

/** Attachments by owner ("production_cost:<id>"), newest first. */
export function useAttachmentsByOwner(): Map<string, Attachment[]> {
  const rows = useRows("attachment");
  if (!lastFiles || lastFiles.rows !== rows) {
    const byOwner = new Map<string, Attachment[]>();
    for (const a of [...rows].sort((x, y) => y.created_at.localeCompare(x.created_at))) {
      const k = `${a.owner_type}:${a.owner_id}`;
      byOwner.set(k, [...(byOwner.get(k) ?? []), a]);
    }
    lastFiles = { rows, byOwner };
  }
  return lastFiles.byOwner;
}

/** "$1,234.56" from cents. */
export const centsText = (c: number) =>
  `${c < 0 ? "−" : ""}$${(Math.abs(c) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** "+$0.75" / "−$1.20" from cents. */
export const signedCents = (c: number) =>
  c === 0 ? "$0.00" : `${c > 0 ? "+" : "−"}$${(Math.abs(c) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** $4.7200 style for a per-unit amount that can have fractions of a cent. */
export const usd4 = (n: number | null | undefined) =>
  n == null
    ? "—"
    : `${n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(n) < 1 && n !== 0 ? 4 : 2 })}`;
/** A date shown short, the app's one way: "Oct 3" (with the year when it isn't this year). */
export const shortDate = dayText;

let lastLanded: { cx: CostIndex; map: Map<string, LandedSource> } | null = null;

/**
 * The landed cost Sell items' margin uses, per built variant (landedCostOf(): actual from the latest closed run, else the
 * planned cost when it's complete). Worked out once per change of the cost index; items without one aren't in it.
 */
export function useLandedCosts(): Map<string, LandedSource> {
  const cx = useCostIndex();
  if (!lastLanded || lastLanded.cx !== cx) {
    const map = new Map<string, LandedSource>();
    for (const i of cx.d.items) {
      if (i.kind !== "sell" || !i.project_id || i.archived) continue;
      const src = landedCostOf(cx, i.id);
      if (src) map.set(i.id, src);
    }
    lastLanded = { cx, map };
  }
  return lastLanded.map;
}
